import { createSelector, createSlice } from "@reduxjs/toolkit";

import { setDeep } from "Libs/objectAccess";
import logger from "Libs/logger";
import { createAppAsyncThunk } from "Store/createAppAsyncThunk";
import { getNewBillingSystemError } from "Hooks/billingSystem/getNewBillingSystemError";

import { getProfilePaymentSourcesRequest } from "Api/billingSystem";

import type { RootState } from "Store/configureStore";
import type { PaymentSourcesResponse } from "Api/billingSystem/types";

// Thunks
export const getProfilePaymentSources = createAppAsyncThunk(
  "billingSystem/paymentSources/getByProfile",
  async (
    { billingProfileId }: { billingProfileId: string },
    { rejectWithValue }
  ) => {
    try {
      return await getProfilePaymentSourcesRequest(billingProfileId);
    } catch (err) {
      logger(err);
      return rejectWithValue({
        error: getNewBillingSystemError(err) ?? "Something went wrong"
      });
    }
  }
);

// State type
type PaymentSourcesState = {
  byProfileId: Record<
    string,
    {
      loading: boolean;
      data?: PaymentSourcesResponse;
      error?: string;
    }
  >;
  paymentMethodModal: {
    isOpen: boolean;
    billingProfileId?: string;
  };
};

const initialState: PaymentSourcesState = {
  byProfileId: {},
  paymentMethodModal: {
    isOpen: false,
    billingProfileId: undefined
  }
};

// Slice
const paymentSourcesSlice = createSlice({
  name: "paymentSources",
  initialState,
  reducers: {
    openPaymentMethodModal: (state, action: { payload: string }) => {
      state.paymentMethodModal.isOpen = true;
      state.paymentMethodModal.billingProfileId = action.payload;
    },
    closePaymentMethodModal: state => {
      state.paymentMethodModal.isOpen = false;
      state.paymentMethodModal.billingProfileId = undefined;
    }
  },
  extraReducers: builder => {
    // Get payment sources by profile
    builder
      .addCase(getProfilePaymentSources.pending, (state, { meta }) => {
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "loading"],
          true
        );
        setDeep(
          state,
          ["byProfileId", meta.arg.billingProfileId, "error"],
          undefined
        );
      })
      .addCase(getProfilePaymentSources.fulfilled, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "data"],
          action.payload
        );
      })
      .addCase(getProfilePaymentSources.rejected, (state, action) => {
        const { billingProfileId } = action.meta.arg;
        setDeep(state, ["byProfileId", billingProfileId, "loading"], false);
        setDeep(
          state,
          ["byProfileId", billingProfileId, "error"],
          action.payload?.error
        );
      });
  }
});

export const { openPaymentMethodModal, closePaymentMethodModal } =
  paymentSourcesSlice.actions;
export const paymentSourcesReducer = paymentSourcesSlice.reducer;

// Selectors
const paymentSourcesStateSelector = (state: RootState) => state.paymentSources;

export const selectProfilePaymentSources = createSelector(
  [
    paymentSourcesStateSelector,
    (_state: RootState, profileId: string) => profileId
  ],
  (paymentSources, profileId) =>
    paymentSources.byProfileId[profileId]?.data?.payment_source
);

export const selectProfilePaymentSourcesLoading = createSelector(
  [
    paymentSourcesStateSelector,
    (_state: RootState, profileId: string) => profileId
  ],
  (paymentSources, profileId) =>
    paymentSources.byProfileId[profileId]?.loading || false
);

export const selectProfilePaymentSourcesError = createSelector(
  [
    paymentSourcesStateSelector,
    (_state: RootState, profileId: string) => profileId
  ],
  (paymentSources, profileId) => paymentSources.byProfileId[profileId]?.error
);

// Payment method modal selectors
export const selectPaymentMethodModalIsOpen = createSelector(
  paymentSourcesStateSelector,
  state => state.paymentMethodModal.isOpen
);

export const selectPaymentMethodModalBillingProfileId = createSelector(
  paymentSourcesStateSelector,
  state => state.paymentMethodModal.billingProfileId
);
